import { TFunction } from "i18next";
import { IMPLEMENTED_SETUP_KEYS } from "../constants";
import { JsonSchema } from "../../../types";
import { ColDef } from "ag-grid-community";
import { collectFields, SETUP_COLUMN_CONFIG } from "./tableHelpers";
import { dynamicValidationMessages } from "../../../translate/dynamicValidationMessages";

export const getTranslatedTooltip = (
  propertyKey: string,
  description: string,
  t: TFunction,
  language?: string,
  namespace?: string,
): string => {
  const lookupKey = `tooltip.${propertyKey}`;

  if (!namespace || language?.startsWith("en")) {
    return description;
  }

  const selectedNamespace = IMPLEMENTED_SETUP_KEYS.find(
    (key) => key.toLowerCase() === namespace.toLowerCase(),
  );

  if (selectedNamespace) {
    const translatedTooltip = t(`${selectedNamespace}:${lookupKey}`, {
      defaultValue: description,
    });
    return translatedTooltip;
  }
  return description;
};

export const getTranslatedDropdownLabel = (
  category: "application" | "setup",
  key: string,
  defaultValue: string,
  t: TFunction,
): string => {
  const lookupKey = `dropdown.${category}.${key.toLowerCase()}`;
  const translated = t(lookupKey, { ns: ["dropdown", category], defaultValue });

  return translated || defaultValue;
};

export const getTranslatedLabel = (
  propertyKey: string,
  field: "title" | "description",
  defaultValue: string,
  t: TFunction,
  language?: string,
): string => {
  const lookupKey = `labels:title.${propertyKey}.${field}`;

  if (language?.startsWith("en")) {
    return defaultValue;
  }

  return t(lookupKey, { defaultValue });
};

export const getTranslatedColDefs = (
  schema: JsonSchema,
  t: TFunction,
): ColDef[] => {
  const addedFields = new Set<string>();
  const allFields = Object.values(schema.sections).flatMap(collectFields);
  const columnOverrides = SETUP_COLUMN_CONFIG[schema.title] ?? {};

  return allFields
    .filter((fieldKey) => {
      const shouldAdd =
        schema.properties[fieldKey] && !addedFields.has(fieldKey);
      if (shouldAdd) addedFields.add(fieldKey);
      return shouldAdd;
    })
    .map((fieldKey) => {
      const property = schema.properties[fieldKey];
      return {
        field: fieldKey,
        headerName: t(`column.${fieldKey}`, { defaultValue: property.title }),
        ...columnOverrides[fieldKey],
      };
    });
};

export const translateDynamicValidationMessage = (
  message: string | string[],
  t: TFunction,
): string | string[] => {
  const messages = Array.isArray(message) ? message : [message];

  const translated = messages.map((msg) => {
    for (const { pattern, key, params } of dynamicValidationMessages) {
      const match = msg.match(pattern);
      if (match) {
        if (params && match.length > 1) {
          const values: Record<string, string> = {};
          params.forEach((param, index) => {
            values[param] = match[index + 1];
          });
          return t(key, { ns: "toastValidation", ...values });
        }
        return t(key, { ns: "toastValidation" });
      }
    }
    return msg;
  });

  return translated.length === 1 ? translated[0] : translated;
};
